import React, { lazy } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Cpu, Brain, Layers } from 'lucide-react';
export function Home() {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Lazy Monk Labs',
    url: 'https://lazymonklabs.com',
    logo: 'https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png',
    description: 'A consciousness engineering lab creating tools like Decode to decode emotional patterns, behavioural loops, and inner architecture.',
    foundingDate: '2024',
    founder: {
      '@type': 'Person',
      name: 'Anish Khandelwal'
    },
    sameAs: ['https://www.linkedin.com/company/lazymonklabs', 'https://www.instagram.com/lazymonklabs'],
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+91-96330-96997',
      contactType: 'Customer Service',
      availableLanguage: 'English'
    }
  };
  const websiteStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Lazy Monk Labs',
    url: 'https://lazymonklabs.com',
    description: 'Consciousness engineering for individuals who want clarity, emotional mastery, and an upgraded inner operating system.',
    publisher: {
      '@type': 'Organization',
      name: 'Lazy Monk Labs'
    }
  };
  const productStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Decode',
    description: 'The Human Pattern Engine - reveals emotional patterns, decodes relationships and conflicts, and gives precise behavioural insight.',
    brand: {
      '@type': 'Brand',
      name: 'Lazy Monk Labs'
    },
    offers: {
      '@type': 'Offer',
      availability: 'https://schema.org/PreOrder',
      url: 'https://lazymonklabs.com/decode'
    }
  };
  return <>
      <Helmet data-id="element-242">
        <title data-id="element-243">Lazy Monk Labs — Consciousness Engineering for Humans</title>
        <meta name="description" content="A consciousness engineering lab creating tools like Decode to decode emotional patterns, behavioural loops, and inner architecture. Truth, engineered." data-id="element-244" />
        <meta name="keywords" content="consciousness engineering, emotional patterns, human behaviour, self-awareness systems, Decode, emotional architecture, behavioural loops, inner operating system" data-id="element-245" />
        <meta name="robots" content="index, follow" data-id="element-246" />
        <meta name="author" content="Lazy Monk Labs" data-id="element-247" />

        {/* Open Graph Tags */}
        <meta property="og:title" content="Lazy Monk Labs — Consciousness Engineering for Humans" data-id="element-248" />
        <meta property="og:description" content="A consciousness engineering lab creating tools like Decode to decode emotional patterns, behavioural loops, and inner architecture. Truth, engineered." data-id="element-249" />
        <meta property="og:type" content="website" data-id="element-250" />
        <meta property="og:url" content="https://lazymonklabs.com" data-id="element-251" />
        <meta property="og:image" content="https://uploadthingy.s3.us-west-1.amazonaws.com/gqbXRsBy5a8YkBwGLByGB5/ChatGPT_Image_Nov_19%2C_2025%2C_08_41_32_AM.png" data-id="element-252" />
        <meta property="og:site_name" content="Lazy Monk Labs" data-id="element-253" />

        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" data-id="element-254" />
        <meta name="twitter:title" content="Lazy Monk Labs — Consciousness Engineering for Humans" data-id="element-255" />
        <meta name="twitter:description" content="A consciousness engineering lab creating tools to decode emotional patterns and behavioural loops. Truth, engineered." data-id="element-256" />
        <meta name="twitter:image" content="https://uploadthingy.s3.us-west-1.amazonaws.com/gqbXRsBy5a8YkBwGLByGB5/ChatGPT_Image_Nov_19%2C_2025%2C_08_41_32_AM.png" data-id="element-257" />

        {/* Canonical URL */}
        <link rel="canonical" href="https://lazymonklabs.com" data-id="element-258" />

        {/* Apple Touch Icon */}
        <link rel="apple-touch-icon" href="/apple-touch-icon.png" data-id="element-259" />

        {/* Google Analytics */}
        <script async src="https://www.googletagmanager.com/gtag/js?id=G-QCXDHW3F0P" data-id="element-260"></script>
        <script data-id="element-261">
          {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', 'G-QCXDHW3F0P');
          `}
        </script>

        {/* Structured Data */}
        <script type="application/ld+json" data-id="element-262">
          {JSON.stringify(structuredData)}
        </script>
        <script type="application/ld+json" data-id="element-263">
          {JSON.stringify(websiteStructuredData)}
        </script>
        <script type="application/ld+json" data-id="element-264">
          {JSON.stringify(productStructuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-265">
        {/* Navigation */}
        <header data-id="element-266">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" role="navigation" aria-label="Main navigation" data-id="element-267">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-268">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" aria-label="Lazy Monk Labs Home" data-id="element-269">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs - Consciousness Engineering Logo" className="h-8 w-auto" width="32" height="32" data-id="element-270" />
                <span className="text-lg font-medium tracking-tight" data-id="element-271">
                  Lazy Monk Labs
                </span>
              </Link>
              <div className="flex items-center gap-8" data-id="element-272">
                <Link to="/decode" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" aria-label="Learn about Decode product" data-id="element-273">
                  Decode
                </Link>
                <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" aria-label="Contact us on WhatsApp" data-id="element-274">
                  Contact
                </a>
              </div>
            </div>
          </nav>
        </header>

        {/* Hero Section */}
        <section className="min-h-screen flex items-center justify-center px-6 pt-20" aria-labelledby="hero-heading" data-id="element-275">
          <div className="max-w-4xl mx-auto text-center" data-id="element-276">
            <h1 id="hero-heading" className="text-5xl md:text-7xl lg:text-8xl font-bold mb-8 leading-tight tracking-tight" style={{
            fontFamily: 'system-ui, -apple-system, sans-serif'
          }} data-id="element-277">
              Truth, engineered.
            </h1>
            <p className="text-lg md:text-xl text-[#F5F5F5]/70 mb-12 max-w-3xl mx-auto leading-relaxed" data-id="element-278">
              Consciousness engineering for individuals who want clarity,
              emotional mastery, and an upgraded inner operating system.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center" data-id="element-279">
              <Link to="/decode" className="px-8 py-4 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-wide uppercase rounded-sm hover:bg-[#F5F5F5]/90 transition-colors" aria-label="Get Decode - The Human Pattern Engine" data-id="element-280">
                Get Decode
              </Link>
              <Link to="/learnmore/founder" className="px-8 py-4 border border-[#F5F5F5] text-[#F5F5F5] font-semibold text-sm tracking-wide uppercase rounded-sm hover:bg-[#F5F5F5]/10 transition-colors" aria-label="Learn more about Lazy Monk Labs vision" data-id="element-281">
                Learn More
              </Link>
            </div>
          </div>
        </section>

        {/* Product 01 - Decode Section */}
        <section className="min-h-screen flex items-center px-6 py-24" aria-labelledby="decode-heading" data-id="element-282">
          <div className="max-w-7xl mx-auto w-full" data-id="element-283">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-284">
              {/* Left: Content */}
              <div data-id="element-285">
                <p className="text-sm text-[#F5F5F5]/50 tracking-widest uppercase mb-8" data-id="element-286">
                  PRODUCT 01
                </p>
                <h2 id="decode-heading" className="text-6xl md:text-8xl font-bold mb-6 tracking-tight" data-id="element-287">
                  Decode
                </h2>
                <p className="text-2xl md:text-3xl text-[#F5F5F5]/60 mb-16" data-id="element-288">
                  The Human Pattern Engine
                </p>
                <ul className="space-y-6 mb-16 text-lg md:text-xl text-[#F5F5F5]/80" aria-label="Decode features" data-id="element-289">
                  <li className="flex items-start gap-4" data-id="element-290">
                    <span className="mt-2" aria-hidden="true" data-id="element-291">
                      •
                    </span>
                    <span data-id="element-292">
                      Reveals emotional patterns and behavioural loops
                    </span>
                  </li>
                  <li className="flex items-start gap-4" data-id="element-293">
                    <span className="mt-2" aria-hidden="true" data-id="element-294">
                      •
                    </span>
                    <span data-id="element-295">Decodes relationships and conflicts</span>
                  </li>
                  <li className="flex items-start gap-4" data-id="element-296">
                    <span className="mt-2" aria-hidden="true" data-id="element-297">
                      •
                    </span>
                    <span data-id="element-298">Gives precise behavioural insight</span>
                  </li>
                </ul>
                <Link to="/decode" className="inline-block px-12 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors" aria-label="Access Decode product page" data-id="element-299">
                  Access Decode
                </Link>
              </div>

              {/* Right: Geometric Pattern */}
              <div className="hidden lg:flex items-center justify-center" aria-hidden="true" data-id="element-300">
                <svg width="400" height="400" viewBox="0 0 400 400" className="opacity-30" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Geometric pattern representing consciousness systems" data-id="element-301">
                  <title data-id="element-302">Consciousness Engineering Pattern</title>
                  <circle cx="200" cy="200" r="180" fill="none" stroke="#F5F5F5" strokeWidth="0.5" data-id="element-303" />
                  <circle cx="200" cy="200" r="120" fill="none" stroke="#F5F5F5" strokeWidth="0.5" data-id="element-304" />
                  <circle cx="200" cy="200" r="60" fill="none" stroke="#F5F5F5" strokeWidth="0.5" data-id="element-305" />
                  <circle cx="200" cy="200" r="4" fill="#F5F5F5" data-id="element-306" />
                  <circle cx="200" cy="20" r="3" fill="#F5F5F5" data-id="element-307" />
                  <circle cx="380" cy="200" r="3" fill="#F5F5F5" data-id="element-308" />
                  <circle cx="200" cy="380" r="3" fill="#F5F5F5" data-id="element-309" />
                  <circle cx="20" cy="200" r="3" fill="#F5F5F5" data-id="element-310" />
                  <circle cx="327" cy="73" r="3" fill="#F5F5F5" data-id="element-311" />
                  <circle cx="327" cy="327" r="3" fill="#F5F5F5" data-id="element-312" />
                  <circle cx="73" cy="327" r="3" fill="#F5F5F5" data-id="element-313" />
                  <circle cx="73" cy="73" r="3" fill="#F5F5F5" data-id="element-314" />
                  <line x1="200" y1="200" x2="200" y2="20" stroke="#F5F5F5" strokeWidth="0.5" opacity="0.3" data-id="element-315" />
                  <line x1="200" y1="200" x2="380" y2="200" stroke="#F5F5F5" strokeWidth="0.5" opacity="0.3" data-id="element-316" />
                  <line x1="200" y1="200" x2="200" y2="380" stroke="#F5F5F5" strokeWidth="0.5" opacity="0.3" data-id="element-317" />
                  <line x1="200" y1="200" x2="20" y2="200" stroke="#F5F5F5" strokeWidth="0.5" opacity="0.3" data-id="element-318" />
                </svg>
              </div>
            </div>
          </div>
        </section>

        {/* What We Build Section */}
        <section className="py-24 px-6" aria-labelledby="what-we-build-heading" data-id="element-319">
          <div className="max-w-7xl mx-auto" data-id="element-320">
            <h2 id="what-we-build-heading" className="text-4xl md:text-5xl font-bold text-center mb-20 tracking-tight" data-id="element-321">
              What We Build
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12" data-id="element-322">
              {/* Card 1 */}
              <article className="text-center" data-id="element-323">
                <div className="flex justify-center mb-6" aria-hidden="true" data-id="element-324">
                  <Cpu size={40} className="text-[#F5F5F5]/60" strokeWidth={1} data-id="element-325" />
                </div>
                <h3 className="text-xl font-semibold mb-4" data-id="element-326">
                  Conscious Systems
                </h3>
                <p className="text-[#F5F5F5]/60 leading-relaxed" data-id="element-327">
                  Infrastructure for self-awareness and inner operating systems
                </p>
              </article>

              {/* Card 2 */}
              <article className="text-center" data-id="element-328">
                <div className="flex justify-center mb-6" aria-hidden="true" data-id="element-329">
                  <Brain size={40} className="text-[#F5F5F5]/60" strokeWidth={1} data-id="element-330" />
                </div>
                <h3 className="text-xl font-semibold mb-4" data-id="element-331">
                  Human Pattern Intelligence
                </h3>
                <p className="text-[#F5F5F5]/60 leading-relaxed" data-id="element-332">
                  Tools that decode human behaviour, emotion, and relationship
                  dynamics
                </p>
              </article>

              {/* Card 3 */}
              <article className="text-center" data-id="element-333">
                <div className="flex justify-center mb-6" aria-hidden="true" data-id="element-334">
                  <Layers size={40} className="text-[#F5F5F5]/60" strokeWidth={1} data-id="element-335" />
                </div>
                <h3 className="text-xl font-semibold mb-4" data-id="element-336">
                  Emotional Architecture Tools
                </h3>
                <p className="text-[#F5F5F5]/60 leading-relaxed" data-id="element-337">
                  Precision instruments for emotional mastery and clarity
                </p>
              </article>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-16 px-6 border-t border-[#2B2B2B]" role="contentinfo" data-id="element-338">
          <div className="max-w-7xl mx-auto" data-id="element-339">
            <div className="flex flex-col items-center gap-6 mb-8" data-id="element-340">
              <div className="flex items-center gap-3" data-id="element-341">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" loading="lazy" data-id="element-342" />
                <span className="text-lg font-medium tracking-tight" data-id="element-343">
                  Lazy Monk Labs
                </span>
              </div>
              <p className="text-sm text-[#F5F5F5]/70 text-center max-w-2xl leading-relaxed" data-id="element-344">
                A consciousness engineering lab building tools to decode
                emotional patterns, behavioural loops, and inner architecture.
                We make self-awareness scalable.
              </p>
              <p className="text-sm text-[#F5F5F5]/50 tracking-wide" data-id="element-345">
                Truth, engineered.
              </p>
            </div>
            <nav className="flex justify-center gap-8 text-sm text-[#F5F5F5]/60 mb-8" aria-label="Footer navigation" data-id="element-346">
              <Link to="/" className="hover:text-[#F5F5F5] transition-colors" data-id="element-347">
                Home
              </Link>
              <Link to="/decode" className="hover:text-[#F5F5F5] transition-colors" data-id="element-348">
                Decode
              </Link>
              <Link to="/learnmore/founder" className="hover:text-[#F5F5F5] transition-colors" data-id="element-349">
                About
              </Link>
              <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="hover:text-[#F5F5F5] transition-colors" data-id="element-350">
                Contact
              </a>
            </nav>
            <div className="text-center text-xs text-[#F5F5F5]/40" data-id="element-351">
              <p data-id="element-352">
                © {new Date().getFullYear()} Lazy Monk Labs. All rights
                reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>;
}